import { useQuery } from '@tanstack/react-query';
import API_ENDPOINTS from './endpoints';
import {
  BUDGET_WORKSPACE_CACHE_TIME_MS,
  BUDGET_WORKSPACE_STALE_TIME_MS,
  getBudgetByIdQueryKey,
  getContractualChargesQueryKey,
  getFilmIncentivesQueryKey,
  getMyBudgetsQueryKey,
  getSwimlaneQueryKey,
  MY_BUDGETS_CACHE_TIME_MS,
  normalizeMyBudgetsPayload,
} from '../budgetWorkspaceCache';

export const useBudgetQueries = {
  useBudgetQuery: ({ config = {}, enabled }) => {
    return useQuery(['get-budget'], () => API_ENDPOINTS.budgets.get_budgets(), {
      retry: false,
      enabled,
      ...config,
    });
  },

  useFolderBudgetQuery: ({ config = {}, enabled }) => {
    return useQuery(['get-folder'], () => API_ENDPOINTS.budgets.get_folders(), {
      retry: false,
      enabled,
      ...config,
    });
  },

  useFolderByIdQuery: ({ config = {}, enabled, folderId }) => {
    return useQuery(
      ['get-folder-by-id', { folderId }],
      () => API_ENDPOINTS.budgets.get_folder_by_id(folderId),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },

  useBudgetByIdQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      getBudgetByIdQueryKey(budgetId),
      () => API_ENDPOINTS.budgets.get_budget_by_id(budgetId),
      {
        retry: false,
        enabled,
        staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
        cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
        ...config,
      }
    );
  },

  useBudgetTypeQuery: ({ config = {}, enabled }) => {
    return useQuery(
      ['get-budget-type'],
      () => API_ENDPOINTS.budgets.get_budget_types(),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },

  useSwimlaneQuery: ({ config = {}, enabled, payload }) => {
    return useQuery(
      getSwimlaneQueryKey(payload),
      () => {
        if (payload.type === 'swimlane')
          return API_ENDPOINTS.budgets.get_swimlanes(payload.id);

        if (payload.type === 'line-item')
          return API_ENDPOINTS.budgets.get_line_items(payload.id);

        if (payload.type === 'line-item-children')
          return API_ENDPOINTS.budgets.get_line_item_children(payload.id);
      },
      {
        retry: false,
        enabled,
        // Ensure query stays active and refetches when invalidated
        refetchOnWindowFocus: false,
        refetchOnMount: true,
        refetchOnReconnect: false,
        staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
        cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
        // Notify on all data changes to ensure components update
        notifyOnChangeProps: 'all',
        ...config,
      }
    );
  },

  useDetailSheetTotalsQuery: ({
    enabled,
    selectedItemId,
    type,
    config = {},
  }) => {
    return useQuery(
      ['detail-sheet-totals', { selectedItemId, type }],
      () => API_ENDPOINTS.budgets.get_detail_sheet_totals(selectedItemId, type),
      {
        retry: false,
        enabled,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        ...config,
      }
    );
  },

  useSwimlaneDetailsQuery: ({ config = {}, enabled, payload }) => {
    return useQuery(
      ['get-swimlanes-details', { ...payload }],
      () => {
        if (payload.type === 'swimlane')
          return API_ENDPOINTS.budgets.get_swimlane_detailed_sheet(payload.id);

        if (payload.type === 'line-item')
          return API_ENDPOINTS.budgets.get_line_item_detailed_sheet(payload.id);

        if (payload.type === 'line-item-children')
          return API_ENDPOINTS.budgets.get_line_item_children_detailed_sheet(
            payload.id
          );
      },
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },

  useContextualSuggestionsQuery: ({ config = {}, enabled }) => {
    return useQuery(
      ['get_contextual_suggestions'],
      () => API_ENDPOINTS.contextual_suggestion.get_contextual_suggestions(),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },

  useUnitTypesQuery: ({ config = {}, enabled } = {}) => {
    return useQuery(
      ['unit-types'],
      () => API_ENDPOINTS.budgets.get_detail_sheet_unit_type(),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },

  usePermissionsQuery: ({ config = {}, enabled } = {}) => {
    return useQuery(
      ['get-permissions'],
      () => API_ENDPOINTS.budgets.get_permissions(),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },

  useCollaboratorsQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      ['get-collaborators', budgetId],
      () => API_ENDPOINTS.budgets.get_collaborators(budgetId),
      {
        retry: false,
        ...config,
        enabled,
      }
    );
  },

  usePublicBudgetLinkSettingsQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      ['public-budget-link-settings', budgetId],
      () => API_ENDPOINTS.budgets.get_public_budget_link_settings(budgetId),
      {
        retry: false,
        ...config,
        enabled,
      }
    );
  },

  usePublicBudgetQuery: ({ config = {}, enabled, token }) => {
    return useQuery(
      ['public-budget', token],
      () => API_ENDPOINTS.budgets.get_public_budget(token),
      {
        retry: false,
        ...config,
        enabled,
      }
    );
  },

  useBudgetPermissionsQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      ['get-budget-permissions', budgetId],
      () => API_ENDPOINTS.budgets.get_budget_permissions(budgetId),
      {
        retry: false,
        ...config,
        enabled,
      }
    );
  },

  useBudgetChangeEventsQuery: ({ config = {}, enabled, payload }) => {
    return useQuery(
      ['get-budget-change-events', payload],
      () => API_ENDPOINTS.budgets.get_change_events(payload),
      {
        retry: false,
        ...config,
        enabled,
      }
    );
  },

  useCellCommentSummaryQuery: ({ config = {}, enabled, payload }) => {
    return useQuery(
      ['cell-comment-summary', payload?.budget, payload?.detail_sheet_type],
      () => API_ENDPOINTS.budgets.get_cell_comment_summary(payload),
      {
        retry: false,
        enabled,
        refetchOnWindowFocus: false,
        ...config,
      }
    );
  },

  useCellCommentsQuery: ({ config = {}, enabled, payload }) => {
    return useQuery(
      [
        'cell-comments',
        payload?.budget,
        payload?.detail_sheet_type,
        payload?.row_id,
        payload?.column_key,
      ],
      () => API_ENDPOINTS.budgets.get_cell_comments(payload),
      {
        retry: false,
        enabled,
        refetchOnWindowFocus: false,
        ...config,
      }
    );
  },

  useBudgetCellCommentThreadQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      ['budget-cell-comment-thread', budgetId],
      () => API_ENDPOINTS.budgets.get_budget_cell_comment_thread(budgetId),
      {
        retry: false,
        enabled,
        refetchOnWindowFocus: false,
        ...config,
      }
    );
  },

  useBudgetCategoriesQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      ['budget-categories', budgetId],
      () => API_ENDPOINTS.budgets.get_budget_categories(budgetId),
      {
        retry: false,
        enabled: enabled && Boolean(budgetId),
        refetchOnWindowFocus: false,
        ...config,
      }
    );
  },

  useBudgetCommentsQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      ['budget-comments', budgetId],
      () => API_ENDPOINTS.budgets.get_budget_comments(budgetId),
      {
        retry: false,
        enabled,
        refetchOnWindowFocus: false,
        ...config,
      }
    );
  },

  useNotificationsQuery: ({ config = {}, enabled } = {}) => {
    return useQuery(
      ['budget-notifications'],
      () => API_ENDPOINTS.budgets.get_notifications(),
      {
        retry: false,
        enabled,
        refetchOnWindowFocus: true,
        ...config,
      }
    );
  },

  useBudgetDataQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      ['get-budget-data', budgetId],
      () => API_ENDPOINTS.budgets.get_budget_data(budgetId),
      {
        retry: false,
        ...config,
        enabled,
      }
    );
  },

  useBudgetDetailsDataQuery: ({ config = {}, enabled, payload }) => {
    const normalizedPayload = normalizeMyBudgetsPayload(payload);
    return useQuery(
      getMyBudgetsQueryKey(normalizedPayload),
      () => API_ENDPOINTS.budgets.get_budget_details_data(normalizedPayload),
      {
        retry: false,
        staleTime: 0,
        cacheTime: MY_BUDGETS_CACHE_TIME_MS,
        refetchOnWindowFocus: false,
        ...config,
        enabled,
      }
    );
  },

  useContractualChargesQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      getContractualChargesQueryKey(budgetId),
      () => API_ENDPOINTS.budgets.get_contractual_charges(budgetId),
      {
        retry: false,
        staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
        cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
        ...config,
        enabled: enabled && Boolean(budgetId),
      }
    );
  },
  useFilmIncentivesQuery: ({ config = {}, enabled, budgetId }) => {
    return useQuery(
      getFilmIncentivesQueryKey(budgetId),
      () => API_ENDPOINTS.budgets.get_film_incentives(budgetId),
      {
        retry: false,
        staleTime: BUDGET_WORKSPACE_STALE_TIME_MS,
        cacheTime: BUDGET_WORKSPACE_CACHE_TIME_MS,
        ...config,
        enabled: enabled && Boolean(budgetId),
      }
    );
  },
};

export const useFringeQuery = {
  useFringeDetailsQuery: ({ config = {}, enabled, filters = {} }) => {
    return useQuery(
      ['get-fringe-details', filters], // Pass filters to query key
      () => API_ENDPOINTS.fringes.list_fringe(filters), // Pass filters to API call
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },
  useDefaultFringeQuery: ({ config = {}, enabled }) => {
    return useQuery(
      ['get-fringe-details'], // Pass filters to query key
      () => API_ENDPOINTS.fringes.default_fringe(), // Pass filters to API call
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },

  useDeleteFringeInfoQuery: ({ config = {}, enabled, payload }) => {
    return useQuery(
      ['get-fringe-details-info'], // Pass filters to query key
      () => API_ENDPOINTS.fringes.delete_fringe_info(payload), // Pass filters to API call
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },

  useGlobalFringeDetailsQuery: ({ config = {}, enabled }) => {
    return useQuery(
      ['get-global-fringe-details'], // Query key for caching and refetching
      () => API_ENDPOINTS.fringes.list_global_fringe(), // API call
      {
        retry: false, // No retry on failure
        enabled, // Conditionally enable the query
        ...config, // Allow passing additional config options
      }
    );
  },
};

export const useGlobalQuery = {
  useGlobalsQuery: ({ config = {}, enabled, filters = {} }) => {
    return useQuery(
      ['budget-globals', filters],
      () => API_ENDPOINTS.globals.list_globals(filters),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },
};

export const useTagQuery = {
  useTagsQuery: ({ config = {}, enabled, filters = {} }) => {
    return useQuery(
      ['budget-tags', filters],
      () => API_ENDPOINTS.tags.list_tags(filters),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },
};

export const useGroupFringeQuery = {
  useGroupFringeDetailsQuery: ({ config = {}, enabled, filters = {} }) => {
    return useQuery(
      ['get-group-fringe-detail', filters], // Query key for caching and refetching
      () => API_ENDPOINTS.groups.list_group(filters), // API call
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },
  useExcludedGroupsQuery: ({ config = {}, enabled, payload }) => {
    return useQuery(
      ['get-exclude-fringe-groups', payload], // Query key for caching and refetching
      () => API_ENDPOINTS.groups.get_groups(payload), // API call
      {
        retry: false, // No retry on failure
        enabled, // Conditionally enable the query
        ...config, // Allow passing additional config options
      }
    );
  },
};

export const useLinkedFringesQuery = {
  useLinkedFringesByGroupQuery: ({ config = {}, enabled, filters = {} }) => {
    return useQuery(
      ['get-linked-fringes-by-group', filters],
      () => API_ENDPOINTS.linked_fringes.get_linked_fringes_by_group(filters),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },
};

export const useAccountSettingQueries = {
  useProfileQuery: ({ config = {}, enabled }) => {
    return useQuery(
      ['user-profile'],
      async () => await API_ENDPOINTS.account_settings.get_user_profile(),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },
  useSearchUserQuery: ({ config = {}, enabled, searchParam } = {}) => {
    return useQuery(
      ['user-search', searchParam],
      () => API_ENDPOINTS.account_settings.search_user(searchParam),
      {
        retry: false,
        enabled,
        refetchOnMount: false,
        ...config,
      }
    );
  },
};
export const useTabsQueries = {
  useOpenTabsQuery: ({ config = {}, enabled }) => {
    return useQuery(
      ['get-open-tabs'],
      () => API_ENDPOINTS.tabs.get_open_tabs(),
      {
        retry: false,
        enabled,
        ...config,
      }
    );
  },
};
